import { useState, useEffect } from 'react';
import api from '../api';
import {
  Bell,
  Send,
  Users,
  User,
  Globe,
  Search,
  CheckCircle,
  XCircle,
  History,
  Clock,
  Calendar,
  MapPin,
  RefreshCw,
  Play,
  Pause,
  Trash2,
  Edit,
  Plus,
  Variable,
  Eye,
  UsersRound,
  MapPinned,
  Loader2,
  Zap,
  Copy,
  Check,
  ChevronRight,
  Gift,
  ChevronDown,
  Undo2,
  Link2,
  ImagePlus,
} from 'lucide-react';
import toast from 'react-hot-toast';
import EngagementSettings from '../components/EngagementSettings';

export default function Notifications() {
  // Tab state
  const [activeTab, setActiveTab] = useState('compose');
  
  // Compose state
  const [targetType, setTargetType] = useState('all');
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [linkUrl, setLinkUrl] = useState('');
  const [imageUrl, setImageUrl] = useState('');
  const [uploadingImage, setUploadingImage] = useState(false);
  const [sending, setSending] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [users, setUsers] = useState([]);
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  
  // Scheduling state
  const [scheduleType, setScheduleType] = useState('immediate');
  const [scheduledDate, setScheduledDate] = useState('');
  const [scheduledTime, setScheduledTime] = useState('');
  const [recurringPattern, setRecurringPattern] = useState('daily');
  const [recurringDays, setRecurringDays] = useState([]);
  const [recurringTime, setRecurringTime] = useState('09:00');
  
  // Geofence state
  const [selectedGeofence, setSelectedGeofence] = useState('');
  const [geofenceLocations, setGeofenceLocations] = useState([]);
  const [geofenceTrigger, setGeofenceTrigger] = useState('enter');
  
  // Groups state
  const [userGroups, setUserGroups] = useState([]);
  const [selectedGroup, setSelectedGroup] = useState('');
  
  // Variables state
  const [availableVariables, setAvailableVariables] = useState([]);
  const [showVariables, setShowVariables] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [preview, setPreview] = useState({ title: '', body: '' });
  
  // Scheduled notifications state
  const [scheduledNotifications, setScheduledNotifications] = useState([]);
  const [loadingScheduled, setLoadingScheduled] = useState(false);
  
  // Recent notifications
  const [recentNotifications, setRecentNotifications] = useState([]);
  
  // Full history state
  const [historyNotifications, setHistoryNotifications] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [historyPage, setHistoryPage] = useState(1);
  const [historyHasMore, setHistoryHasMore] = useState(true);
  const [copiedMessageId, setCopiedMessageId] = useState(null);

  // Attach Reward state
  const [attachReward, setAttachReward] = useState(false);
  const [rewardForm, setRewardForm] = useState({
    title: '',
    description: '',
    rewardValue: '',
    rewardValueType: 'dollar',
    icon: 'gift',
    requiresStaffAuth: true,
    expiresAfterDays: '',
    expiresAfterHours: '',
  });

  // Load data on mount
  useEffect(() => {
    fetchRecentActivity();
    fetchVariables();
    fetchGeofenceLocations();
    fetchUserGroups();
    fetchScheduledNotifications();
  }, []);
  
  // Load history when tab changes to history
  useEffect(() => {
    if (activeTab === 'history' && historyNotifications.length === 0) {
      fetchHistory(1);
    }
  }, [activeTab]);

  // User search with debounce
  useEffect(() => {
    const timer = setTimeout(() => searchUsers(searchQuery), 300);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  const searchUsers = async (query) => {
    if (!query || query.length < 2) {
      setUsers([]);
      return;
    }
    try {
      setLoadingUsers(true);
      const response = await api.get(`/admin/users?search=${query}&limit=10`);
      setUsers(response.data.users || []);
    } catch (err) {
      console.error('Search failed:', err);
    } finally {
      setLoadingUsers(false);
    }
  };

  const fetchRecentActivity = async () => {
    try {
      const response = await api.get('/admin/activity?action=notification_sent,notification_sent_with_reward,scheduled_notification_sent&limit=10');
      setRecentNotifications(response.data.logs || []);
    } catch (err) {
      console.error('Failed to fetch activity:', err);
    }
  };

  const fetchHistory = async (page = 1, append = false) => {
    try {
      setLoadingHistory(true);
      const response = await api.get(`/admin/activity?action=notification_sent,notification_sent_with_reward,scheduled_notification_sent&limit=20&page=${page}`);
      const logs = response.data.logs || [];
      
      if (append) {
        setHistoryNotifications(prev => [...prev, ...logs]);
      } else {
        setHistoryNotifications(logs);
      }
      
      setHistoryHasMore(logs.length === 20);
      setHistoryPage(page);
    } catch (err) {
      console.error('Failed to fetch history:', err);
    } finally {
      setLoadingHistory(false);
    }
  };

  const copyMessage = (notif) => {
    const details = notif.details || {};
    const text = `Title: ${details.title || ''}\n\nBody: ${details.body || ''}`;
    navigator.clipboard.writeText(text);
    setCopiedMessageId(notif.id);
    toast.success('Message copied to clipboard');
    setTimeout(() => setCopiedMessageId(null), 2000);
  };

  const fetchVariables = async () => {
    try {
      const response = await api.get('/admin/notifications/variables');
      setAvailableVariables(response.data.variables || []);
    } catch (err) {
      console.error('Failed to fetch variables:', err);
    }
  };

  const fetchGeofenceLocations = async () => {
    try {
      const response = await api.get('/admin/geofence-locations');
      setGeofenceLocations(response.data.locations || []);
    } catch (err) {
      console.error('Failed to fetch geofences:', err);
    }
  };

  const fetchUserGroups = async () => {
    try {
      const response = await api.get('/admin/user-groups');
      setUserGroups(response.data.groups || []);
    } catch (err) {
      console.error('Failed to fetch groups:', err);
    }
  };

  const fetchScheduledNotifications = async () => {
    setLoadingScheduled(true);
    try {
      const response = await api.get('/admin/scheduled-notifications');
      setScheduledNotifications(response.data.notifications || []);
    } catch (err) {
      console.error('Failed to fetch scheduled:', err);
    } finally {
      setLoadingScheduled(false);
    }
  };

  const toggleUser = (user) => {
    setSelectedUsers((prev) =>
      prev.find((u) => u.id === user.id)
        ? prev.filter((u) => u.id !== user.id)
        : [...prev, user]
    );
  };

  const insertVariable = (variable) => {
    setBody((prev) => prev + variable);
  };

  const handlePreview = async () => {
    try {
      const response = await api.post('/admin/notifications/preview', { title, body });
      setPreview(response.data.preview);
      setShowPreview(true);
    } catch (err) {
      toast.error('Failed to generate preview');
    }
  };

  const handleImageSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) { toast.error('Please choose an image file'); return; }
    if (file.size > 5 * 1024 * 1024) { toast.error('Image must be under 5 MB'); return; }
    try {
      setUploadingImage(true);
      const base64 = await new Promise((resolve, reject) => {
        const r = new FileReader();
        r.onload = () => resolve(r.result);
        r.onerror = reject;
        r.readAsDataURL(file);
      });
      const resp = await api.post('/admin/notifications/upload-image', { imageBase64: base64, imageMimeType: file.type });
      setImageUrl(resp.data.imageUrl);
      toast.success('Image uploaded');
    } catch (err) {
      console.error('Image upload error:', err);
      toast.error(err?.response?.data?.error || 'Failed to upload image');
    } finally {
      setUploadingImage(false);
      e.target.value = '';
    }
  };

  const handleSend = async () => {
    if (!title.trim() || !body.trim()) {
      toast.error('Title and message are required');
      return;
    }

    if (targetType === 'selected' && selectedUsers.length === 0) {
      toast.error('Select at least one user');
      return;
    }

    if (targetType === 'group' && !selectedGroup) {
      toast.error('Select a user group');
      return;
    }

    if (attachReward && !rewardForm.title.trim()) {
      toast.error('Reward title is required when attaching a reward');
      return;
    }

    try {
      setSending(true);
      
      if (scheduleType === 'immediate') {
        let payload = {
          title,
          body,
          targetType: targetType === 'selected' ? 'users' : targetType === 'group' ? 'group' : 'all',
          targetIds: targetType === 'selected' ? selectedUsers.map((u) => u.id) : [],
          groupId: targetType === 'group' ? selectedGroup : null,
        };

        if (linkUrl.trim()) payload.linkUrl = linkUrl.trim();
        if (imageUrl) payload.imageUrl = imageUrl;

        // Use the reward-enabled endpoint if a reward is attached
        if (attachReward && rewardForm.title.trim()) {
          payload.attachReward = true;
          payload.rewardTitle = rewardForm.title.trim();
          payload.rewardDescription = rewardForm.description.trim() || undefined;
          payload.rewardValue = rewardForm.rewardValue ? parseFloat(rewardForm.rewardValue) : undefined;
          payload.rewardValueType = rewardForm.rewardValueType;
          payload.rewardIcon = rewardForm.icon;
          payload.requiresStaffAuth = rewardForm.requiresStaffAuth;
          payload.expiresAfterDays = rewardForm.expiresAfterDays ? parseInt(rewardForm.expiresAfterDays) : undefined;
          payload.expiresAfterHours = rewardForm.expiresAfterHours ? parseInt(rewardForm.expiresAfterHours) : undefined;
        }

        const endpoint = (attachReward && rewardForm.title.trim())
          ? '/admin/notifications/send-with-reward'
          : '/admin/notifications/send';
        const response = await api.post(endpoint, payload);
        const stats = response.data.stats;
        const rewardMsg = response.data.reward ? ` + reward "${response.data.reward.rewardTitle}" attached` : '';
        toast.success(`Sent to ${stats.success} of ${stats.tokensFound} devices${rewardMsg}`);
        
      } else {
        let payload = {
          title,
          body,
          targetType: targetType === 'selected' ? 'users' : targetType === 'group' ? 'group' : 'all',
          targetIds: targetType === 'selected' ? selectedUsers.map((u) => u.id) : [],
          targetGroupId: targetType === 'group' ? selectedGroup : null,
          scheduleType,
        };

        if (linkUrl.trim()) payload.linkUrl = linkUrl.trim();
        if (imageUrl) payload.imageUrl = imageUrl;

        if (scheduleType === 'scheduled') {
          payload.scheduledAt = `${scheduledDate}T${scheduledTime}`;
        } else if (scheduleType === 'recurring') {
          payload.recurringPattern = recurringPattern;
          payload.recurringDays = recurringDays;
          payload.recurringTime = recurringTime;
        } else if (scheduleType === 'geofence') {
          payload.geofenceLocationId = selectedGeofence;
          payload.geofenceTrigger = geofenceTrigger;
        }

        await api.post('/admin/scheduled-notifications', payload);
        toast.success('Notification scheduled successfully');
        fetchScheduledNotifications();
      }

      setTitle('');
      setBody('');
      setLinkUrl('');
      setImageUrl('');
      setSelectedUsers([]);
      setSelectedGroup('');
      setAttachReward(false);
      setRewardForm({ title: '', description: '', rewardValue: '', rewardValueType: 'dollar', icon: 'gift', requiresStaffAuth: true, expiresAfterDays: '', expiresAfterHours: '' });
      fetchRecentActivity();
      
    } catch (err) {
      console.error('Send error:', err);
      toast.error('Failed to send notification');
    } finally {
      setSending(false);
    }
  };

  const toggleScheduledNotification = async (id, currentStatus) => {
    try {
      await api.put(`/admin/scheduled-notifications/${id}`, {
        isActive: !currentStatus,
      });
      toast.success(currentStatus ? 'Notification paused' : 'Notification resumed');
      fetchScheduledNotifications();
    } catch (err) {
      toast.error('Failed to update notification');
    }
  };

  const deleteScheduledNotification = async (id) => {
    if (!confirm('Are you sure you want to delete this notification?')) return;
    try {
      await api.delete(`/admin/scheduled-notifications/${id}`);
      toast.success('Notification deleted');
      fetchScheduledNotifications();
    } catch (err) {
      toast.error('Failed to delete notification');
    }
  };

  const formatSchedule = (notif) => {
    if (notif.scheduleType === 'scheduled' || notif.scheduleType === 'once') {
      return new Date(notif.scheduledAt).toLocaleString();
    }
    if (notif.scheduleType === 'recurring') {
      const days = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
      const selectedDays = (notif.recurringDays || []).map(d => days[d]).join(', ');
      return `${notif.recurringPattern} at ${notif.recurringTime}${selectedDays ? ` (${selectedDays})` : ''}`;
    }
    if (notif.scheduleType === 'geofence') {
      return `On ${notif.geofenceTrigger} geofence`;
    }
    return 'Immediate';
  };

  const DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Bell className="text-amber-500" size={28} />
          <h1 className="text-2xl font-bold text-white">Notification Center</h1>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 border-b border-gray-700 pb-2">
        {[
          { id: 'compose', label: 'Compose', icon: Send },
          { id: 'history', label: 'History', icon: History },
          { id: 'scheduled', label: 'Scheduled', icon: Clock },
          { id: 'automated', label: 'Automated', icon: Zap },
          { id: 'geofences', label: 'Geofences', icon: MapPin },
          { id: 'groups', label: 'Groups', icon: UsersRound },
        ].map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`flex items-center gap-2 px-4 py-2 rounded-t-lg font-medium ${
              activeTab === tab.id
                ? 'bg-gray-800 text-amber-400 border-b-2 border-amber-400'
                : 'text-gray-400 hover:text-white'
            }`}
          >
            <tab.icon size={18} />
            {tab.label}
          </button>
        ))}
      </div>

      {/* Compose Tab */}
      {activeTab === 'compose' && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 space-y-6">
            {/* Target Selection */}
            <div className="bg-gray-800 rounded-xl p-6">
              <h2 className="text-lg font-semibold text-white mb-4">Send To</h2>
              <div className="grid grid-cols-3 gap-3 mb-4">
                {[
                  { id: 'all', label: 'All Users', icon: Globe },
                  { id: 'selected', label: 'Select Users', icon: Users },
                  { id: 'group', label: 'User Group', icon: UsersRound },
                ].map((target) => (
                  <button
                    key={target.id}
                    onClick={() => setTargetType(target.id)}
                    className={`flex items-center justify-center gap-2 py-3 rounded-lg border ${
                      targetType === target.id
                        ? 'bg-amber-500/20 border-amber-500 text-amber-400'
                        : 'border-gray-600 text-gray-400 hover:border-gray-500'
                    }`}
                  >
                    <target.icon size={18} />
                    <span className="text-sm">{target.label}</span>
                  </button>
                ))}
              </div>

              {targetType === 'selected' && (
                <div className="space-y-3">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" size={18} />
                    <input
                      type="text"
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      placeholder="Search users by email or name..."
                      className="w-full pl-10 pr-4 py-3 bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-500"
                    />
                  </div>
                  {users.length > 0 && (
                    <div className="bg-gray-700 rounded-lg divide-y divide-gray-600 max-h-48 overflow-y-auto">
                      {users.map((user) => (
                        <button
                          key={user.id}
                          onClick={() => toggleUser(user)}
                          className={`w-full flex items-center gap-3 p-3 hover:bg-gray-600 ${
                            selectedUsers.find((u) => u.id === user.id) ? 'bg-amber-500/10' : ''
                          }`}
                        >
                          <User size={16} className="text-gray-400" />
                          <span className="text-white">{user.email}</span>
                          {user.firstName && <span className="text-gray-500">({user.firstName})</span>}
                        </button>
                      ))}
                    </div>
                  )}
                  {selectedUsers.length > 0 && (
                    <div className="flex flex-wrap gap-2">
                      {selectedUsers.map((user) => (
                        <span key={user.id} className="flex items-center gap-1 px-3 py-1 bg-amber-500/20 text-amber-400 rounded-full text-sm">
                          {user.email}
                          <button onClick={() => toggleUser(user)} className="hover:text-amber-300"><XCircle size={14} /></button>
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {targetType === 'group' && (
                <select
                  value={selectedGroup}
                  onChange={(e) => setSelectedGroup(e.target.value)}
                  className="w-full px-4 py-3 bg-gray-700 border border-gray-600 rounded-lg text-white"
                >
                  <option value="">Select a group...</option>
                  {userGroups.map((group) => (
                    <option key={group.id} value={group.id}>{group.name} ({group.memberCount} members)</option>
                  ))}
                </select>
              )}
            </div>

            {/* Message Compose */}
            <div className="bg-gray-800 rounded-xl p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-semibold text-white">Compose Message</h2>
                <button onClick={handlePreview} className="flex items-center gap-1 px-3 py-1.5 bg-gray-700 text-gray-400 hover:text-white rounded text-sm">
                  <Eye size={14} />Preview
                </button>
              </div>

              {/* Always-visible Quick Variables */}
              <div className="mb-4 p-3 bg-gradient-to-r from-amber-500/10 to-amber-600/5 border border-amber-500/30 rounded-lg">
                <div className="flex items-center gap-2 mb-2">
                  <Variable size={14} className="text-amber-400" />
                  <p className="text-xs text-amber-400 font-medium">Quick Insert Variables (click to add to message):</p>
                </div>
                <div className="flex flex-wrap gap-2">
                  {availableVariables.map((v) => (
                    <button 
                      key={v.key} 
                      onClick={() => insertVariable(v.key)} 
                      className="px-2.5 py-1.5 bg-gray-700 hover:bg-amber-500/30 text-amber-400 rounded text-xs font-mono border border-gray-600 hover:border-amber-500/50 transition-all" 
                      title={`${v.description} (e.g., ${v.example})`}
                    >
                      {v.key}
                    </button>
                  ))}
                </div>
                <p className="text-xs text-gray-500 mt-2">💡 Tip: Variables are replaced with user-specific data when sent</p>
              </div>

              {showPreview && (
                <div className="mb-4 p-3 bg-green-500/10 border border-green-500/30 rounded-lg">
                  <p className="text-xs text-green-400 mb-2">Preview (sample user):</p>
                  <p className="text-white font-medium">{preview.title}</p>
                  <p className="text-gray-300 text-sm">{preview.body}</p>
                </div>
              )}

              <div className="space-y-4">
                <div>
                  <label className="block text-sm text-gray-400 mb-1">Title</label>
                  <input type="text" value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Notification title..." className="w-full px-4 py-3 bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-500" maxLength={50} />
                  <p className="text-xs text-gray-500 mt-1">{title.length}/50</p>
                </div>
                <div>
                  <label className="block text-sm text-gray-400 mb-1">Message</label>
                  <textarea value={body} onChange={(e) => setBody(e.target.value)} placeholder="Notification message..." rows={4} className="w-full px-4 py-3 bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-500 resize-none" maxLength={200} />
                  <p className="text-xs text-gray-500 mt-1">{body.length}/200</p>
                </div>
                <div>
                  <label className="block text-sm text-gray-400 mb-1 flex items-center gap-1.5"><Link2 size={14} /> Link (optional)</label>
                  <input type="url" value={linkUrl} onChange={(e) => setLinkUrl(e.target.value)} placeholder="https://edhcasino.com/promo" className="w-full px-4 py-3 bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-500" />
                  <p className="text-xs text-gray-500 mt-1">Tapping the notification opens this link inside the app.</p>
                </div>
                <div>
                  <label className="block text-sm text-gray-400 mb-1 flex items-center gap-1.5"><ImagePlus size={14} /> Image (optional)</label>
                  {imageUrl ? (
                    <div className="relative inline-block">
                      <img src={imageUrl} alt="Push" className="max-h-40 rounded-lg border border-gray-600" />
                      <button type="button" onClick={() => setImageUrl('')} className="absolute -top-2 -right-2 bg-gray-900 border border-gray-600 rounded-full p-1 text-gray-300 hover:text-white" title="Remove image"><XCircle size={18} /></button>
                    </div>
                  ) : (
                    <label className={`flex items-center justify-center gap-2 w-full px-4 py-3 bg-gray-700 border border-dashed border-gray-600 rounded-lg cursor-pointer text-gray-300 hover:bg-gray-600 ${uploadingImage ? 'opacity-60 pointer-events-none' : ''}`}>
                      {uploadingImage ? <><Loader2 size={16} className="animate-spin" /> Uploading…</> : <><ImagePlus size={16} /> Upload image</>}
                      <input type="file" accept="image/*" className="hidden" onChange={handleImageSelect} disabled={uploadingImage} />
                    </label>
                  )}
                  <p className="text-xs text-gray-500 mt-1">Shows on Android now; on iOS after the next app update. Under 5 MB.</p>
                </div>
              </div>
            </div>

            {/* Attach Reward */}
            <div className="bg-gray-800 rounded-xl p-6">
              <button
                onClick={() => setAttachReward(!attachReward)}
                className="w-full flex items-center justify-between"
              >
                <div className="flex items-center gap-3">
                  <div className={`p-2 rounded-lg ${attachReward ? 'bg-emerald-500/20' : 'bg-gray-700'}`}>
                    <Gift size={20} className={attachReward ? 'text-emerald-400' : 'text-gray-400'} />
                  </div>
                  <div className="text-left">
                    <h2 className="text-lg font-semibold text-white">Attach Reward</h2>
                    <p className="text-xs text-gray-500">Bundle a redeemable reward with this notification</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  {attachReward && <span className="text-xs text-emerald-400 font-medium">Reward attached</span>}
                  <div className={`w-11 h-6 rounded-full transition-colors ${attachReward ? 'bg-emerald-500' : 'bg-gray-600'} relative`}>
                    <div className={`absolute top-0.5 w-5 h-5 rounded-full bg-white shadow transition-transform ${attachReward ? 'translate-x-5' : 'translate-x-0.5'}`} />
                  </div>
                </div>
              </button>

              {attachReward && (
                <div className="mt-4 pt-4 border-t border-gray-700 space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="col-span-2">
                      <label className="block text-sm text-gray-400 mb-1">Reward Title *</label>
                      <input
                        type="text"
                        value={rewardForm.title}
                        onChange={(e) => setRewardForm({ ...rewardForm, title: e.target.value })}
                        placeholder="e.g. $25 Free Play"
                        className="w-full px-4 py-3 bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-500"
                      />
                    </div>
                    <div className="col-span-2">
                      <label className="block text-sm text-gray-400 mb-1">Description</label>
                      <input
                        type="text"
                        value={rewardForm.description}
                        onChange={(e) => setRewardForm({ ...rewardForm, description: e.target.value })}
                        placeholder="Optional details about the reward"
                        className="w-full px-4 py-3 bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm text-gray-400 mb-1">Value</label>
                      <div className="flex gap-2">
                        <input
                          type="number"
                          value={rewardForm.rewardValue}
                          onChange={(e) => setRewardForm({ ...rewardForm, rewardValue: e.target.value })}
                          placeholder="25"
                          className="flex-1 px-4 py-3 bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-500"
                        />
                        <select
                          value={rewardForm.rewardValueType}
                          onChange={(e) => setRewardForm({ ...rewardForm, rewardValueType: e.target.value })}
                          className="px-3 py-3 bg-gray-700 border border-gray-600 rounded-lg text-white"
                        >
                          <option value="dollar">$</option>
                          <option value="percent">%</option>
                        </select>
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm text-gray-400 mb-1">Expires After</label>
                      <div className="flex items-center gap-2">
                        <input
                          type="number"
                          value={rewardForm.expiresAfterDays}
                          onChange={(e) => setRewardForm({ ...rewardForm, expiresAfterDays: e.target.value, expiresAfterHours: '' })}
                          placeholder="—"
                          className="flex-1 px-4 py-3 bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-500"
                        />
                        <span className="text-gray-500 text-sm whitespace-nowrap">days</span>
                        <span className="text-gray-600 text-sm">or</span>
                        <input
                          type="number"
                          value={rewardForm.expiresAfterHours || ''}
                          onChange={(e) => setRewardForm({ ...rewardForm, expiresAfterHours: e.target.value, expiresAfterDays: '' })}
                          placeholder="—"
                          className="flex-1 px-4 py-3 bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-500"
                        />
                        <span className="text-gray-500 text-sm whitespace-nowrap">hours</span>
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-6">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={rewardForm.requiresStaffAuth}
                        onChange={(e) => setRewardForm({ ...rewardForm, requiresStaffAuth: e.target.checked })}
                        className="w-4 h-4 rounded border-gray-600 bg-gray-700 text-amber-500 focus:ring-amber-500"
                      />
                      <span className="text-sm text-gray-300">Requires Staff Auth</span>
                    </label>
                    <div className="flex items-center gap-2">
                      <label className="text-sm text-gray-400">Icon:</label>
                      <select
                        value={rewardForm.icon}
                        onChange={(e) => setRewardForm({ ...rewardForm, icon: e.target.value })}
                        className="px-3 py-1.5 bg-gray-700 border border-gray-600 rounded-lg text-white text-sm"
                      >
                        <option value="gift">🎁 Gift</option>
                        <option value="dollar-sign">💵 Dollar</option>
                        <option value="star">⭐ Star</option>
                        <option value="zap">⚡ Zap</option>
                        <option value="trophy">🏆 Trophy</option>
                        <option value="percent">💰 Percent</option>
                      </select>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Scheduling Options */}
            <div className="bg-gray-800 rounded-xl p-6">
              <h2 className="text-lg font-semibold text-white mb-4">Delivery</h2>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
                {[
                  { id: 'immediate', label: 'Send Now', icon: Send },
                  { id: 'scheduled', label: 'Schedule', icon: Calendar },
                  { id: 'recurring', label: 'Recurring', icon: RefreshCw },
                  { id: 'geofence', label: 'Geofence', icon: MapPin },
                ].map((type) => (
                  <button key={type.id} onClick={() => setScheduleType(type.id)} className={`flex items-center justify-center gap-2 py-3 rounded-lg border ${scheduleType === type.id ? 'bg-amber-500/20 border-amber-500 text-amber-400' : 'border-gray-600 text-gray-400 hover:border-gray-500'}`}>
                    <type.icon size={18} /><span className="text-sm">{type.label}</span>
                  </button>
                ))}
              </div>

              {scheduleType === 'scheduled' && (
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm text-gray-400 mb-1">Date</label>
                    <input type="date" value={scheduledDate} onChange={(e) => setScheduledDate(e.target.value)} className="w-full px-4 py-3 bg-gray-700 border border-gray-600 rounded-lg text-white" />
                  </div>
                  <div>
                    <label className="block text-sm text-gray-400 mb-1">Time</label>
                    <input type="time" value={scheduledTime} onChange={(e) => setScheduledTime(e.target.value)} className="w-full px-4 py-3 bg-gray-700 border border-gray-600 rounded-lg text-white" />
                  </div>
                </div>
              )}

              {scheduleType === 'recurring' && (
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm text-gray-400 mb-1">Frequency</label>
                    <select value={recurringPattern} onChange={(e) => setRecurringPattern(e.target.value)} className="w-full px-4 py-3 bg-gray-700 border border-gray-600 rounded-lg text-white">
                      <option value="daily">Daily</option>
                      <option value="weekly">Weekly</option>
                      <option value="monthly">Monthly</option>
                    </select>
                  </div>
                  {recurringPattern === 'weekly' && (
                    <div>
                      <label className="block text-sm text-gray-400 mb-2">Days</label>
                      <div className="flex gap-2">
                        {DAYS.map((day, i) => (
                          <button key={day} onClick={() => setRecurringDays(prev => prev.includes(i) ? prev.filter(d => d !== i) : [...prev, i])} className={`px-3 py-2 rounded ${recurringDays.includes(i) ? 'bg-amber-500 text-black' : 'bg-gray-700 text-gray-400'}`}>{day}</button>
                        ))}
                      </div>
                    </div>
                  )}
                  <div>
                    <label className="block text-sm text-gray-400 mb-1">Time</label>
                    <input type="time" value={recurringTime} onChange={(e) => setRecurringTime(e.target.value)} className="w-full px-4 py-3 bg-gray-700 border border-gray-600 rounded-lg text-white" />
                  </div>
                </div>
              )}

              {scheduleType === 'geofence' && (
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm text-gray-400 mb-1">Location</label>
                    <select value={selectedGeofence} onChange={(e) => setSelectedGeofence(e.target.value)} className="w-full px-4 py-3 bg-gray-700 border border-gray-600 rounded-lg text-white">
                      <option value="">Select a location...</option>
                      {geofenceLocations.map((loc) => (<option key={loc.id} value={loc.id}>{loc.name} ({loc.radius}m)</option>))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm text-gray-400 mb-1">Trigger</label>
                    <div className="flex gap-4">
                      <label className="flex items-center gap-2 text-gray-300"><input type="radio" value="enter" checked={geofenceTrigger === 'enter'} onChange={() => setGeofenceTrigger('enter')} className="accent-amber-500" />On Enter</label>
                      <label className="flex items-center gap-2 text-gray-300"><input type="radio" value="exit" checked={geofenceTrigger === 'exit'} onChange={() => setGeofenceTrigger('exit')} className="accent-amber-500" />On Exit</label>
                    </div>
                  </div>
                </div>
              )}

              <button onClick={handleSend} disabled={sending || !title || !body || (attachReward && !rewardForm.title.trim())} className={`w-full mt-4 flex items-center justify-center gap-2 py-3 rounded-lg font-semibold ${sending || !title || !body || (attachReward && !rewardForm.title.trim()) ? 'bg-gray-700 text-gray-500 cursor-not-allowed' : attachReward ? 'bg-emerald-600 text-white hover:bg-emerald-500' : 'bg-amber-500 text-black hover:bg-amber-400'}`}>
                {scheduleType === 'immediate' ? (attachReward ? <Gift size={18} /> : <Send size={18} />) : <Clock size={18} />}
                {sending ? 'Sending...' : scheduleType === 'immediate' ? (attachReward ? 'Send with Reward' : 'Send Now') : 'Schedule Notification'}
              </button>
            </div>
          </div>

          {/* Right Column - Recent */}
          <div className="bg-gray-800 rounded-xl p-6">
            <div 
              className="flex items-center justify-between mb-4 cursor-pointer hover:opacity-80 transition-opacity"
              onClick={() => setActiveTab('history')}
            >
              <div className="flex items-center gap-2">
                <History size={20} className="text-amber-500" />
                <h2 className="text-lg font-semibold text-white">Recent Sends</h2>
              </div>
              <div className="flex items-center gap-1 text-amber-400 text-sm">
                <span>View All</span>
                <ChevronRight size={16} />
              </div>
            </div>
            <div className="space-y-3">
              {recentNotifications.length > 0 ? (
                recentNotifications.map((notif) => {
                  const details = notif.details || {};
                  return (
                    <div key={notif.id} className="p-3 bg-gray-700/50 rounded-lg hover:bg-gray-700/70 transition-colors">
                      <div className="flex items-start justify-between">
                        <div className="flex-1 min-w-0">
                          <p className="text-white font-medium truncate">{details.title || 'Notification'}</p>
                          <p className="text-sm text-gray-400 line-clamp-1">{details.body || ''}</p>
                        </div>
                        <div className="flex items-center gap-2 ml-2">
                          <div className="flex items-center gap-1 text-xs">
                            <CheckCircle size={12} className="text-green-400" />
                            <span className="text-green-400">{details.successCount || 0}</span>
                          </div>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              copyMessage(notif);
                            }}
                            className="p-1.5 bg-gray-600/50 text-gray-300 rounded hover:bg-gray-600 transition-colors"
                            title="Copy message"
                          >
                            {copiedMessageId === notif.id ? <Check size={14} className="text-green-400" /> : <Copy size={14} />}
                          </button>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              setTitle(details.title || '');
                              setBody(details.body || '');
                              setScheduleType('immediate');
                              setTargetType('all');
                              window.scrollTo({ top: 0, behavior: 'smooth' });
                            }}
                            className="p-1.5 bg-amber-500/20 text-amber-400 rounded hover:bg-amber-500/30 transition-colors"
                            title="Reuse this message"
                          >
                            <RefreshCw size={14} />
                          </button>
                          {details.campaignId && (
                            <button
                              onClick={async (e) => {
                                e.stopPropagation();
                                if (!window.confirm("Recall this message? It's removed from recipients' in-app inbox and we'll best-effort dismiss it from their device. Anyone who already saw it can't un-see it.")) return;
                                try {
                                  const r = await api.post('/admin/notifications/recall', { campaignId: details.campaignId });
                                  toast.success(`Recalled — cleared from ${r.data.removedFromInbox} inbox(es)`);
                                  fetchRecentActivity();
                                } catch (err) {
                                  toast.error(err.response?.data?.error || 'Recall failed');
                                }
                              }}
                              className="p-1.5 bg-red-500/20 text-red-400 rounded hover:bg-red-500/30 transition-colors"
                              title="Recall this message"
                            >
                              <Undo2 size={14} />
                            </button>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center justify-between mt-2 text-xs text-gray-500">
                        <span>{details.targetType === 'all' ? 'All Users' : `${details.targetCount || 0} users`}</span>
                        <span>{new Date(notif.createdAt).toLocaleString()}</span>
                      </div>
                    </div>
                  );
                })
              ) : (
                <p className="text-center text-gray-500 py-8">No notifications sent yet</p>
              )}
            </div>
          </div>
        </div>
      )}

      {/* History Tab */}
      {activeTab === 'history' && (
        <div className="bg-gray-800 rounded-xl p-6">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-2">
              <History size={20} className="text-amber-500" />
              <h2 className="text-lg font-semibold text-white">Notification History</h2>
            </div>
            <button 
              onClick={() => fetchHistory(1)} 
              className="flex items-center gap-2 px-3 py-2 bg-gray-700 text-gray-300 rounded-lg hover:bg-gray-600"
            >
              <RefreshCw size={16} className={loadingHistory ? 'animate-spin' : ''} />
              Refresh
            </button>
          </div>
          
          {loadingHistory && historyNotifications.length === 0 ? (
            <div className="text-center py-12 text-gray-400">Loading...</div>
          ) : historyNotifications.length > 0 ? (
            <div className="space-y-3">
              {historyNotifications.map((notif) => {
                const details = notif.details || {};
                return (
                  <div key={notif.id} className="p-4 bg-gray-700/50 rounded-lg">
                    <div className="flex items-start justify-between">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <p className="text-white font-medium">{details.title || 'Notification'}</p>
                          {details.scheduleType && (
                            <span className={`text-xs px-2 py-0.5 rounded-full ${
                              details.scheduleType === 'geofence' ? 'bg-blue-500/20 text-blue-400' :
                              details.scheduleType === 'recurring' ? 'bg-purple-500/20 text-purple-400' :
                              details.scheduleType === 'once' ? 'bg-cyan-500/20 text-cyan-400' :
                              'bg-gray-500/20 text-gray-400'
                            }`}>
                              {details.scheduleType === 'geofence' ? '📍 Geofence' :
                               details.scheduleType === 'recurring' ? '🔄 Recurring' :
                               details.scheduleType === 'once' ? '⏰ Scheduled' : details.scheduleType}
                            </span>
                          )}
                          {notif.action === 'notification_sent_with_reward' && (
                            <span className="text-xs px-2 py-0.5 rounded-full bg-amber-500/20 text-amber-400">🎁 Reward</span>
                          )}
                        </div>
                        <p className="text-sm text-gray-400 mt-1">{details.body || ''}</p>
                      </div>
                      <div className="flex items-center gap-2 ml-4">
                        <button
                          onClick={() => copyMessage(notif)}
                          className="p-2 bg-gray-600/50 text-gray-300 rounded hover:bg-gray-600 transition-colors"
                          title="Copy message"
                        >
                          {copiedMessageId === notif.id ? <Check size={16} className="text-green-400" /> : <Copy size={16} />}
                        </button>
                        <button
                          onClick={() => {
                            setTitle(details.title || '');
                            setBody(details.body || '');
                            setScheduleType('immediate');
                            setTargetType('all');
                            setActiveTab('compose');
                            window.scrollTo({ top: 0, behavior: 'smooth' });
                          }}
                          className="p-2 bg-amber-500/20 text-amber-400 rounded hover:bg-amber-500/30 transition-colors"
                          title="Reuse this message"
                        >
                          <RefreshCw size={16} />
                        </button>
                      </div>
                    </div>
                    <div className="flex items-center gap-4 mt-3 text-sm">
                      <div className="flex items-center gap-1">
                        <CheckCircle size={14} className="text-green-400" />
                        <span className="text-green-400">{details.successCount || 0} sent</span>
                      </div>
                      {(details.failureCount > 0 || details.failCount > 0) && (
                        <div className="flex items-center gap-1">
                          <XCircle size={14} className="text-red-400" />
                          <span className="text-red-400">{details.failureCount || details.failCount} failed</span>
                        </div>
                      )}
                      <span className="text-gray-500">
                        {details.targetType === 'all' ? 'All Users' : `${details.targetCount || 0} targeted`}
                      </span>
                      <span className="text-gray-500 ml-auto">
                        {new Date(notif.createdAt).toLocaleString()}
                      </span>
                    </div>
                    {notif.adminUser && (
                      <div className="mt-2 text-xs text-gray-500">
                        Sent by: {notif.adminUser.firstName} {notif.adminUser.lastName}
                      </div>
                    )}
                    {details.targetedUsers && details.targetedUsers.length > 0 && (
                      <div className="mt-2 flex items-center gap-2 flex-wrap">
                        <span className="text-xs text-gray-500">To:</span>
                        {details.targetedUsers.map((user, idx) => (
                          <span key={user.id || idx} className="text-xs px-2 py-0.5 bg-gray-600/50 text-gray-300 rounded-full">
                            {user.name}
                          </span>
                        ))}
                      </div>
                    )}
                    {details.targetType === 'all' && (
                      <div className="mt-2 flex items-center gap-2">
                        <span className="text-xs text-gray-500">To:</span>
                        <span className="text-xs px-2 py-0.5 bg-amber-500/10 text-amber-400 rounded-full">All Users</span>
                      </div>
                    )}
                  </div>
                );
              })}
              
              {historyHasMore && (
                <button
                  onClick={() => fetchHistory(historyPage + 1, true)}
                  disabled={loadingHistory}
                  className="w-full py-3 text-amber-400 hover:text-amber-300 font-medium disabled:opacity-50"
                >
                  {loadingHistory ? 'Loading...' : 'Load More'}
                </button>
              )}
            </div>
          ) : (
            <p className="text-center text-gray-500 py-12">No notifications sent yet</p>
          )}
        </div>
      )}

      {/* Scheduled Tab */}
      {activeTab === 'scheduled' && (
        <div className="bg-gray-800 rounded-xl p-6">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-lg font-semibold text-white">Scheduled & Recurring Notifications</h2>
            <button onClick={fetchScheduledNotifications} className="flex items-center gap-2 px-3 py-2 bg-gray-700 text-gray-300 rounded-lg hover:bg-gray-600">
              <RefreshCw size={16} className={loadingScheduled ? 'animate-spin' : ''} />Refresh
            </button>
          </div>
          {loadingScheduled ? (
            <div className="text-center py-12 text-gray-400">Loading...</div>
          ) : scheduledNotifications.length > 0 ? (
            <div className="space-y-3">
              {scheduledNotifications.map((notif) => (
                <div key={notif.id} className={`p-4 rounded-lg border ${notif.isActive ? 'bg-gray-700/50 border-gray-600' : 'bg-gray-700/20 border-gray-700'}`}>
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        {notif.scheduleType === 'recurring' && <RefreshCw size={14} className="text-blue-400" />}
                        {notif.scheduleType === 'scheduled' && <Calendar size={14} className="text-purple-400" />}
                        {notif.scheduleType === 'geofence' && <MapPin size={14} className="text-green-400" />}
                        <span className={`text-xs px-2 py-0.5 rounded ${notif.isActive ? 'bg-green-500/20 text-green-400' : 'bg-gray-600 text-gray-400'}`}>{notif.isActive ? 'Active' : 'Paused'}</span>
                        <span className="text-xs text-gray-500">{notif.scheduleType}</span>
                      </div>
                      <p className="text-white font-medium">{notif.title}</p>
                      <p className="text-sm text-gray-400 line-clamp-1">{notif.body}</p>
                      <p className="text-xs text-gray-500 mt-2">{formatSchedule(notif)}</p>
                    </div>
                    <div className="flex items-center gap-2 ml-4">
                      <button onClick={() => toggleScheduledNotification(notif.id, notif.isActive)} className={`p-2 rounded-lg ${notif.isActive ? 'bg-amber-500/20 text-amber-400 hover:bg-amber-500/30' : 'bg-green-500/20 text-green-400 hover:bg-green-500/30'}`} title={notif.isActive ? 'Pause' : 'Resume'}>
                        {notif.isActive ? <Pause size={16} /> : <Play size={16} />}
                      </button>
                      <button onClick={() => deleteScheduledNotification(notif.id)} className="p-2 rounded-lg bg-red-500/20 text-red-400 hover:bg-red-500/30" title="Delete"><Trash2 size={16} /></button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-12">
              <Clock size={48} className="mx-auto text-gray-600 mb-4" />
              <p className="text-gray-400">No scheduled notifications</p>
              <p className="text-gray-500 text-sm mt-1">Create one from the Compose tab</p>
            </div>
          )}
        </div>
      )}

      {/* Geofences Tab */}
      {activeTab === 'geofences' && (
        <GeofenceManager locations={geofenceLocations} groups={userGroups} onRefresh={fetchGeofenceLocations} />
      )}

      {/* Automated Engagement Tab */}
      {activeTab === 'automated' && (
        <EngagementSettings />
      )}

      {/* Groups Tab */}
      {activeTab === 'groups' && (
        <GroupManager groups={userGroups} onRefresh={fetchUserGroups} />
      )}
    </div>
  );
}

// Geocoding helper - uses our backend proxy to avoid CORS issues
async function geocodeAddress(address) {
  try {
    console.log('[Geocode] Searching for:', address);
    const response = await api.get(`/admin/geofence-locations/geocode?q=${encodeURIComponent(address)}`);
    console.log('[Geocode] Results:', response.data.results);
    return response.data.results || [];
  } catch (err) {
    console.error('Geocoding error:', err);
    return [];
  }
}

// Reusable player-targeting picker: All / Segments (groups) / Specific players.
// value = { targetType, groupIds, userIds }; onChange(next).
function EntryTargeting({ value, onChange, groups = [] }) {
  const [userQuery, setUserQuery] = useState('');
  const [userResults, setUserResults] = useState([]);
  const [picked, setPicked] = useState({}); // userId -> label cache

  const searchUsers = async (q) => {
    if (!q || q.length < 2) { setUserResults([]); return; }
    try {
      const res = await api.get(`/admin/users?search=${encodeURIComponent(q)}&limit=10`);
      setUserResults(res.data.users || res.data || []);
    } catch { setUserResults([]); }
  };

  const set = (patch) => onChange({ ...value, ...patch });
  const toggleGroup = (id) => {
    const cur = new Set(value.groupIds || []);
    cur.has(id) ? cur.delete(id) : cur.add(id);
    set({ groupIds: [...cur] });
  };
  const addUser = (u) => {
    const cur = new Set(value.userIds || []);
    cur.add(u.id);
    setPicked((p) => ({ ...p, [u.id]: `${u.firstName || ''} ${u.lastName || ''}`.trim() || u.email }));
    set({ userIds: [...cur] });
    setUserQuery(''); setUserResults([]);
  };
  const removeUser = (id) => set({ userIds: (value.userIds || []).filter((x) => x !== id) });

  return (
    <div className="space-y-2">
      <div className="flex gap-4 text-sm text-gray-300">
        {['all', 'groups', 'users'].map((t) => (
          <label key={t} className="flex items-center gap-1.5">
            <input type="radio" checked={value.targetType === t} onChange={() => set({ targetType: t })} className="accent-amber-500" />
            {t === 'all' ? 'All players' : t === 'groups' ? 'Segments' : 'Specific players'}
          </label>
        ))}
      </div>
      {value.targetType === 'groups' && (
        <div className="flex flex-wrap gap-2">
          {groups.length === 0 && <span className="text-xs text-gray-500">No segments defined (create them in the Groups tab).</span>}
          {groups.map((g) => (
            <button key={g.id} type="button" onClick={() => toggleGroup(g.id)}
              className={`px-2 py-1 rounded text-xs border ${(value.groupIds || []).includes(g.id) ? 'bg-amber-500 text-black border-amber-500' : 'bg-gray-700 text-gray-300 border-gray-600'}`}>
              {g.name}
            </button>
          ))}
        </div>
      )}
      {value.targetType === 'users' && (
        <div>
          <input type="text" value={userQuery}
            onChange={(e) => { setUserQuery(e.target.value); searchUsers(e.target.value); }}
            className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded text-white text-sm"
            placeholder="Search players by name or email..." />
          {userResults.length > 0 && (
            <div className="mt-1 bg-gray-700 rounded divide-y divide-gray-600 max-h-40 overflow-y-auto">
              {userResults.map((u) => (
                <button key={u.id} type="button" onClick={() => addUser(u)} className="w-full text-left px-3 py-2 hover:bg-gray-600 text-sm text-white">
                  {`${u.firstName || ''} ${u.lastName || ''}`.trim() || u.email} <span className="text-gray-500">{u.email}</span>
                </button>
              ))}
            </div>
          )}
          <div className="flex flex-wrap gap-2 mt-2">
            {(value.userIds || []).map((id) => (
              <span key={id} className="px-2 py-1 rounded text-xs bg-gray-700 text-gray-200 border border-gray-600 flex items-center gap-1">
                {picked[id] || id.slice(0, 8)}
                <button type="button" onClick={() => removeUser(id)} className="text-red-400">×</button>
              </span>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// Status tiers for per-rule tier targeting. Keys match PlayerClubMember
// current_status_tier; labels reflect the app's display naming (tier key
// 'chairman' shows as "Boardmember", 'legend' shows as "Chairman").
const REWARD_TIERS = [
  { key: 'member', label: 'Member' },
  { key: 'vip', label: 'VIP' },
  { key: 'elite', label: 'Elite' },
  { key: 'ambassador', label: 'Ambassador' },
  { key: 'chairman', label: 'Boardmember' },
  { key: 'legend', label: 'Chairman' },
];

// Repeatable reward-offer rules: send different rewards to different tiers /
// segments / players from one geofence. Highest tier wins on multi-match.
function RewardOffersEditor({ offers = [], onChange, rewards = [], groups = [] }) {
  const [userQuery, setUserQuery] = useState('');
  const [userResults, setUserResults] = useState([]);
  const [activeSearchIdx, setActiveSearchIdx] = useState(null);
  const [picked, setPicked] = useState({});

  const update = (idx, patch) => onChange(offers.map((o, i) => (i === idx ? { ...o, ...patch } : o)));
  const addOffer = () => onChange([...offers, { id: `offer-${Date.now()}`, targetType: 'all', tier: '', groupIds: [], userIds: [], rewardId: '', offerTitle: '', offerBody: '', claimWindowHours: 6 }]);
  const removeOffer = (idx) => onChange(offers.filter((_, i) => i !== idx));

  const searchUsers = async (q, idx) => {
    setActiveSearchIdx(idx);
    if (!q || q.length < 2) { setUserResults([]); return; }
    try { const res = await api.get(`/admin/users?search=${encodeURIComponent(q)}&limit=10`); setUserResults(res.data.users || res.data || []); } catch { setUserResults([]); }
  };
  const addUser = (idx, u) => {
    const cur = new Set(offers[idx].userIds || []); cur.add(u.id);
    setPicked((p) => ({ ...p, [u.id]: `${u.firstName || ''} ${u.lastName || ''}`.trim() || u.email }));
    update(idx, { userIds: [...cur] }); setUserQuery(''); setUserResults([]);
  };
  const removeUser = (idx, id) => update(idx, { userIds: (offers[idx].userIds || []).filter((x) => x !== id) });
  const toggleGroup = (idx, gid) => {
    const cur = new Set(offers[idx].groupIds || []);
    cur.has(gid) ? cur.delete(gid) : cur.add(gid);
    update(idx, { groupIds: [...cur] });
  };

  return (
    <div className="space-y-3">
      {offers.length === 0 && <p className="text-xs text-gray-500">No reward offers. Add a rule to send a claimable reward on entry.</p>}
      {offers.map((o, idx) => (
        <div key={o.id || idx} className="p-3 bg-gray-800 border border-gray-600 rounded space-y-2">
          <div className="flex items-center justify-between">
            <span className="text-xs text-gray-400">Rule {idx + 1}</span>
            <button type="button" onClick={() => removeOffer(idx)} className="text-red-400 text-xs">Remove</button>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <select value={o.targetType} onChange={(e) => update(idx, { targetType: e.target.value })} className="px-2 py-1.5 bg-gray-700 border border-gray-600 rounded text-white text-sm">
              <option value="all">All players</option>
              <option value="tier">Tier</option>
              <option value="segment">Segment</option>
              <option value="players">Specific players</option>
            </select>
            <select value={o.rewardId} onChange={(e) => update(idx, { rewardId: e.target.value })} className="px-2 py-1.5 bg-gray-700 border border-gray-600 rounded text-white text-sm">
              <option value="">Select reward…</option>
              {rewards.map((r) => (<option key={r.id} value={r.id}>{r.title}</option>))}
            </select>
          </div>
          {o.targetType === 'tier' && (
            <select value={o.tier || ''} onChange={(e) => update(idx, { tier: e.target.value })} className="w-full px-2 py-1.5 bg-gray-700 border border-gray-600 rounded text-white text-sm">
              <option value="">Select tier…</option>
              {REWARD_TIERS.map((t) => (<option key={t.key} value={t.key}>{t.label}</option>))}
            </select>
          )}
          {o.targetType === 'segment' && (
            <div className="flex flex-wrap gap-1.5">
              {groups.length === 0 && <span className="text-xs text-gray-500">No segments defined.</span>}
              {groups.map((g) => (
                <button key={g.id} type="button" onClick={() => toggleGroup(idx, g.id)} className={`px-2 py-1 rounded text-xs border ${(o.groupIds || []).includes(g.id) ? 'bg-amber-500 text-black border-amber-500' : 'bg-gray-700 text-gray-300 border-gray-600'}`}>{g.name}</button>
              ))}
            </div>
          )}
          {o.targetType === 'players' && (
            <div>
              <input type="text" value={activeSearchIdx === idx ? userQuery : ''} onChange={(e) => { setUserQuery(e.target.value); searchUsers(e.target.value, idx); }} className="w-full px-2 py-1.5 bg-gray-700 border border-gray-600 rounded text-white text-sm" placeholder="Search players…" />
              {activeSearchIdx === idx && userResults.length > 0 && (
                <div className="mt-1 bg-gray-700 rounded divide-y divide-gray-600 max-h-32 overflow-y-auto">
                  {userResults.map((u) => (
                    <button key={u.id} type="button" onClick={() => addUser(idx, u)} className="w-full text-left px-2 py-1.5 hover:bg-gray-600 text-xs text-white">{`${u.firstName || ''} ${u.lastName || ''}`.trim() || u.email}</button>
                  ))}
                </div>
              )}
              <div className="flex flex-wrap gap-1.5 mt-1">
                {(o.userIds || []).map((id) => (
                  <span key={id} className="px-2 py-1 rounded text-xs bg-gray-700 text-gray-200 border border-gray-600 flex items-center gap-1">{picked[id] || id.slice(0, 8)}<button type="button" onClick={() => removeUser(idx, id)} className="text-red-400">×</button></span>
                ))}
              </div>
            </div>
          )}
          <div className="grid grid-cols-3 gap-2">
            <input type="text" value={o.offerTitle || ''} onChange={(e) => update(idx, { offerTitle: e.target.value })} className="px-2 py-1.5 bg-gray-700 border border-gray-600 rounded text-white text-sm" placeholder="Offer title" />
            <input type="text" value={o.offerBody || ''} onChange={(e) => update(idx, { offerBody: e.target.value })} className="px-2 py-1.5 bg-gray-700 border border-gray-600 rounded text-white text-sm" placeholder="Offer message" />
            <input type="number" value={o.claimWindowHours ?? ''} onChange={(e) => update(idx, { claimWindowHours: e.target.value === '' ? null : parseInt(e.target.value) })} className="px-2 py-1.5 bg-gray-700 border border-gray-600 rounded text-white text-sm" placeholder="Claim hrs" />
          </div>
        </div>
      ))}
      <button type="button" onClick={addOffer} className="px-3 py-1.5 bg-gray-700 text-amber-400 rounded text-sm border border-gray-600 hover:bg-gray-600">+ Add reward rule</button>
      <p className="text-[11px] text-gray-500">If a player matches multiple rules, the highest tier wins; players matching no rule get the plain push above.</p>
    </div>
  );
}

// Geofence Manager Component with Address Lookup
function GeofenceManager({ locations, groups = [], onRefresh }) {
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const DEFAULT_FORM = {
    name: '',
    description: '',
    latitude: '',
    longitude: '',
    radius: 500,
    // Which transitions to record/notify on. Exit tracking is off by default.
    triggerOnEnter: true,
    triggerOnExit: false,
    // Staff alerts on entry (feature 1)
    staffNotifyEnabled: false,
    staffNotifyPlayerTargetType: 'all',
    staffNotifyPlayerGroupIds: [],
    staffNotifyPlayerUserIds: [],
    staffNotifyRecipientUserIds: [],
    staffNotifyCooldownMinutes: 360,
    // Player push + claimable reward (feature 2)
    playerNotifyEnabled: false,
    playerTargetType: 'all',
    playerGroupIds: [],
    playerUserIds: [],
    rewardId: '',
    rewardOfferTitle: '',
    rewardOfferBody: '',
    rewardClaimWindowHours: 6,
    playerNotifyCooldownMinutes: 1440,
    defaultTitle: '',
    defaultBody: '',
    rewardOffers: [],
  };
  const [form, setForm] = useState(DEFAULT_FORM);
  const [addressQuery, setAddressQuery] = useState('');
  const [addressResults, setAddressResults] = useState([]);
  const [searchingAddress, setSearchingAddress] = useState(false);
  const [rewards, setRewards] = useState([]);
  const [staffQuery, setStaffQuery] = useState('');
  const [staffResults, setStaffResults] = useState([]);
  const [staffPicked, setStaffPicked] = useState({}); // userId -> label

  useEffect(() => {
    api.get('/admin/rewards')
      .then((r) => setRewards(r.data.rewards || r.data || []))
      .catch(() => setRewards([]));
  }, []);

  const searchStaff = async (q) => {
    if (!q || q.length < 2) { setStaffResults([]); return; }
    try {
      const res = await api.get(`/admin/users?search=${encodeURIComponent(q)}&limit=10`);
      setStaffResults(res.data.users || res.data || []);
    } catch { setStaffResults([]); }
  };
  const addStaff = (u) => {
    const cur = new Set(form.staffNotifyRecipientUserIds || []);
    cur.add(u.id);
    setStaffPicked((p) => ({ ...p, [u.id]: `${u.firstName || ''} ${u.lastName || ''}`.trim() || u.email }));
    setForm({ ...form, staffNotifyRecipientUserIds: [...cur] });
    setStaffQuery(''); setStaffResults([]);
  };
  const removeStaff = (id) => setForm({ ...form, staffNotifyRecipientUserIds: (form.staffNotifyRecipientUserIds || []).filter((x) => x !== id) });

  const resetForm = () => {
    setForm(DEFAULT_FORM);
    setEditingId(null);
    setAddressQuery('');
    setAddressResults([]);
    setStaffPicked({});
  };

  const searchAddress = async () => {
    if (!addressQuery || addressQuery.length < 3) return;
    setSearchingAddress(true);
    try {
      const results = await geocodeAddress(addressQuery);
      setAddressResults(results);
    } finally {
      setSearchingAddress(false);
    }
  };

  const selectAddress = (result) => {
    setForm({
      ...form,
      latitude: result.latitude.toFixed(6),
      longitude: result.longitude.toFixed(6),
      description: form.description || result.displayName.split(',').slice(0, 3).join(','),
    });
    setAddressResults([]);
    setAddressQuery('');
  };

  const handleSubmit = async () => {
    if (!form.name || !form.latitude || !form.longitude) {
      toast.error('Name and coordinates are required');
      return;
    }
    try {
      const payload = {
        ...form,
        latitude: parseFloat(form.latitude),
        longitude: parseFloat(form.longitude),
        radius: parseInt(form.radius) || 500,
        rewardId: form.rewardId || null,
        rewardClaimWindowHours: form.rewardClaimWindowHours === '' ? null : parseInt(form.rewardClaimWindowHours),
        staffNotifyCooldownMinutes: parseInt(form.staffNotifyCooldownMinutes) || 360,
        playerNotifyCooldownMinutes: parseInt(form.playerNotifyCooldownMinutes) || 1440,
        // Clean reward offer rules: drop incomplete rows, normalize claim window.
        rewardOffers: (form.rewardOffers || [])
          .filter((o) => o && o.rewardId)
          .map((o) => ({
            id: o.id,
            targetType: o.targetType || 'all',
            tier: o.targetType === 'tier' ? (o.tier || null) : null,
            groupIds: o.targetType === 'segment' ? (o.groupIds || []) : [],
            userIds: o.targetType === 'players' ? (o.userIds || []) : [],
            rewardId: o.rewardId,
            offerTitle: o.offerTitle || null,
            offerBody: o.offerBody || null,
            claimWindowHours: (o.claimWindowHours === '' || o.claimWindowHours == null) ? null : parseInt(o.claimWindowHours),
          })),
      };

      if (editingId) {
        await api.put(`/admin/geofence-locations/${editingId}`, payload);
        toast.success('Location updated');
      } else {
        await api.post('/admin/geofence-locations', payload);
        toast.success('Location created');
      }
      resetForm();
      setShowForm(false);
      onRefresh();
    } catch (err) {
      console.error('Save error:', err);
      toast.error(err.response?.data?.error || 'Failed to save location');
    }
  };

  const handleEdit = (loc) => {
    setForm({
      ...DEFAULT_FORM,
      name: loc.name || '',
      description: loc.description || '',
      latitude: loc.latitude?.toString() || '',
      longitude: loc.longitude?.toString() || '',
      radius: loc.radius || 500,
      triggerOnEnter: loc.triggerOnEnter !== false,
      triggerOnExit: !!loc.triggerOnExit,
      staffNotifyEnabled: !!loc.staffNotifyEnabled,
      staffNotifyPlayerTargetType: loc.staffNotifyPlayerTargetType || 'all',
      staffNotifyPlayerGroupIds: loc.staffNotifyPlayerGroupIds || [],
      staffNotifyPlayerUserIds: loc.staffNotifyPlayerUserIds || [],
      staffNotifyRecipientUserIds: loc.staffNotifyRecipientUserIds || [],
      staffNotifyCooldownMinutes: loc.staffNotifyCooldownMinutes ?? 360,
      playerNotifyEnabled: !!loc.playerNotifyEnabled,
      playerTargetType: loc.playerTargetType || 'all',
      playerGroupIds: loc.playerGroupIds || [],
      playerUserIds: loc.playerUserIds || [],
      rewardId: loc.rewardId || '',
      rewardOffers: (Array.isArray(loc.rewardOffers) && loc.rewardOffers.length)
        ? loc.rewardOffers
        : (loc.rewardId ? [{ id: `offer-${Date.now()}`, targetType: 'all', tier: '', groupIds: [], userIds: [], rewardId: loc.rewardId, offerTitle: loc.rewardOfferTitle || '', offerBody: loc.rewardOfferBody || '', claimWindowHours: loc.rewardClaimWindowHours ?? 6 }] : []),
      rewardOfferTitle: loc.rewardOfferTitle || '',
      rewardOfferBody: loc.rewardOfferBody || '',
      rewardClaimWindowHours: loc.rewardClaimWindowHours ?? 6,
      playerNotifyCooldownMinutes: loc.playerNotifyCooldownMinutes ?? 1440,
      defaultTitle: loc.defaultTitle || '',
      defaultBody: loc.defaultBody || '',
    });
    setEditingId(loc.id);
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (!confirm('Delete this geofence location?')) return;
    try {
      await api.delete(`/admin/geofence-locations/${id}`);
      toast.success('Location deleted');
      onRefresh();
    } catch (err) {
      toast.error('Failed to delete');
    }
  };

  const handleToggle = async (id) => {
    try {
      await api.post(`/admin/geofence-locations/${id}/toggle`);
      toast.success('Location toggled');
      onRefresh();
    } catch (err) {
      toast.error('Failed to toggle');
    }
  };

  return (
    <div className="bg-gray-800 rounded-xl p-6">
      <div className="flex items-center justify-between mb-2">
        <h2 className="text-lg font-semibold text-white">Geofence Locations</h2>
        <button onClick={() => { resetForm(); setShowForm(!showForm); }} className="flex items-center gap-2 px-4 py-2 bg-amber-500 text-black rounded-lg hover:bg-amber-400">
          <Plus size={18} />{showForm ? 'Cancel' : 'Add Location'}
        </button>
      </div>
      <p className="text-sm text-gray-400 mb-6">Define locations that can trigger notifications. Use the Compose tab to create notifications for these locations.</p>

      {showForm && (
        <div className="mb-6 p-4 bg-gray-700/50 rounded-lg space-y-4">
          {/* Address Search */}
          <div>
            <label className="block text-sm text-gray-400 mb-1">
              <MapPinned size={14} className="inline mr-1" />
              Search by Address
            </label>
            <div className="flex gap-2">
              <input
                type="text"
                value={addressQuery}
                onChange={(e) => setAddressQuery(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && searchAddress()}
                className="flex-1 px-3 py-2 bg-gray-700 border border-gray-600 rounded text-white"
                placeholder="Enter address, city, or place name..."
              />
              <button onClick={searchAddress} disabled={searchingAddress} className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-500 disabled:opacity-50">
                {searchingAddress ? <Loader2 size={18} className="animate-spin" /> : <Search size={18} />}
              </button>
            </div>
            {addressResults.length > 0 && (
              <div className="mt-2 bg-gray-700 rounded divide-y divide-gray-600 max-h-48 overflow-y-auto">
                {addressResults.map((result, i) => (
                  <button
                    key={i}
                    onClick={() => selectAddress(result)}
                    className="w-full text-left p-3 hover:bg-gray-600"
                  >
                    <p className="text-white text-sm">{result.displayName}</p>
                    <p className="text-xs text-gray-500">{result.latitude.toFixed(6)}, {result.longitude.toFixed(6)}</p>
                  </button>
                ))}
              </div>
            )}
          </div>

          <div className="border-t border-gray-600 pt-4">
            <p className="text-xs text-gray-500 mb-3">Or enter coordinates manually:</p>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm text-gray-400 mb-1">Name *</label>
              <input type="text" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded text-white" placeholder="Location name" />
            </div>
            <div>
              <label className="block text-sm text-gray-400 mb-1">Radius (meters)</label>
              <input type="number" value={form.radius} onChange={(e) => setForm({ ...form, radius: e.target.value })} className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded text-white" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm text-gray-400 mb-1">Latitude *</label>
              <input type="text" value={form.latitude} onChange={(e) => setForm({ ...form, latitude: e.target.value })} className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded text-white" placeholder="38.6857" />
            </div>
            <div>
              <label className="block text-sm text-gray-400 mb-1">Longitude *</label>
              <input type="text" value={form.longitude} onChange={(e) => setForm({ ...form, longitude: e.target.value })} className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded text-white" placeholder="-121.0760" />
            </div>
          </div>
          <div>
            <label className="block text-sm text-gray-400 mb-1">Track transitions</label>
            <div className="flex gap-4">
              <label className="flex items-center gap-2 text-sm text-gray-300">
                <input type="checkbox" checked={form.triggerOnEnter} onChange={(e) => setForm({ ...form, triggerOnEnter: e.target.checked })} className="h-4 w-4 rounded" />
                Record entries
              </label>
              <label className="flex items-center gap-2 text-sm text-gray-300">
                <input type="checkbox" checked={form.triggerOnExit} onChange={(e) => setForm({ ...form, triggerOnExit: e.target.checked })} className="h-4 w-4 rounded" />
                Record exits
              </label>
            </div>
            <p className="text-xs text-gray-500 mt-1">Exits are off by default. Turn on "Record exits" to log when players leave this zone (the report will then show both). Note: exit detection depends on the app getting a location update after the player leaves, so exits can lag or be missed if the app is closed.</p>
          </div>
          <div>
            <label className="block text-sm text-gray-400 mb-1">Description</label>
            <input type="text" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded text-white" placeholder="Optional description" />
          </div>
          
          {/* ── Staff alerts on entry (feature 1) ── */}
          <div className="border-t border-gray-600 pt-4 space-y-3">
            <label className="flex items-center gap-2 text-white font-medium">
              <input type="checkbox" checked={form.staffNotifyEnabled} onChange={(e) => setForm({ ...form, staffNotifyEnabled: e.target.checked })} className="accent-amber-500" />
              <Bell size={16} className="text-amber-400" /> Alert staff when players enter
            </label>
            {form.staffNotifyEnabled && (
              <div className="pl-6 space-y-3">
                <div>
                  <p className="text-xs text-gray-400 mb-1">Which players' entries alert staff:</p>
                  <EntryTargeting
                    value={{ targetType: form.staffNotifyPlayerTargetType, groupIds: form.staffNotifyPlayerGroupIds, userIds: form.staffNotifyPlayerUserIds }}
                    onChange={(v) => setForm({ ...form, staffNotifyPlayerTargetType: v.targetType, staffNotifyPlayerGroupIds: v.groupIds, staffNotifyPlayerUserIds: v.userIds })}
                    groups={groups}
                  />
                </div>
                <div>
                  <p className="text-xs text-gray-400 mb-1">Staff recipients (app accounts to alert):</p>
                  <input type="text" value={staffQuery}
                    onChange={(e) => { setStaffQuery(e.target.value); searchStaff(e.target.value); }}
                    className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded text-white text-sm"
                    placeholder="Search staff by name or email..." />
                  {staffResults.length > 0 && (
                    <div className="mt-1 bg-gray-700 rounded divide-y divide-gray-600 max-h-40 overflow-y-auto">
                      {staffResults.map((u) => (
                        <button key={u.id} type="button" onClick={() => addStaff(u)} className="w-full text-left px-3 py-2 hover:bg-gray-600 text-sm text-white">
                          {`${u.firstName || ''} ${u.lastName || ''}`.trim() || u.email} <span className="text-gray-500">{u.email}</span>
                        </button>
                      ))}
                    </div>
                  )}
                  <div className="flex flex-wrap gap-2 mt-2">
                    {(form.staffNotifyRecipientUserIds || []).map((id) => (
                      <span key={id} className="px-2 py-1 rounded text-xs bg-gray-700 text-gray-200 border border-gray-600 flex items-center gap-1">
                        {staffPicked[id] || id.slice(0, 8)}
                        <button type="button" onClick={() => removeStaff(id)} className="text-red-400">×</button>
                      </span>
                    ))}
                  </div>
                </div>
                <div>
                  <label className="block text-xs text-gray-400 mb-1">Alert throttle per player (minutes)</label>
                  <input type="number" value={form.staffNotifyCooldownMinutes} onChange={(e) => setForm({ ...form, staffNotifyCooldownMinutes: e.target.value })} className="w-40 px-3 py-2 bg-gray-700 border border-gray-600 rounded text-white text-sm" />
                </div>
              </div>
            )}
          </div>

          {/* ── Player push + claimable reward (feature 2) ── */}
          <div className="border-t border-gray-600 pt-4 space-y-3">
            <label className="flex items-center gap-2 text-white font-medium">
              <input type="checkbox" checked={form.playerNotifyEnabled} onChange={(e) => setForm({ ...form, playerNotifyEnabled: e.target.checked })} className="accent-amber-500" />
              <Send size={16} className="text-amber-400" /> Notify players who enter (optional reward)
            </label>
            {form.playerNotifyEnabled && (
              <div className="pl-6 space-y-3">
                <div>
                  <p className="text-xs text-gray-400 mb-1">Which players receive the push:</p>
                  <EntryTargeting
                    value={{ targetType: form.playerTargetType, groupIds: form.playerGroupIds, userIds: form.playerUserIds }}
                    onChange={(v) => setForm({ ...form, playerTargetType: v.targetType, playerGroupIds: v.groupIds, playerUserIds: v.userIds })}
                    groups={groups}
                  />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs text-gray-400 mb-1">Push title</label>
                    <input type="text" value={form.defaultTitle} onChange={(e) => setForm({ ...form, defaultTitle: e.target.value })} className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded text-white text-sm" placeholder="Welcome!" />
                  </div>
                  <div>
                    <label className="block text-xs text-gray-400 mb-1">Push message</label>
                    <input type="text" value={form.defaultBody} onChange={(e) => setForm({ ...form, defaultBody: e.target.value })} className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded text-white text-sm" placeholder="Thanks for stopping by!" />
                  </div>
                </div>
                <div>
                  <label className="block text-xs text-gray-400 mb-1">Reward offers (claimable in-app) — optional, targeted by tier / segment / player</label>
                  <RewardOffersEditor
                    offers={form.rewardOffers}
                    onChange={(next) => setForm({ ...form, rewardOffers: next })}
                    rewards={rewards}
                    groups={groups}
                  />
                </div>
                <div>
                  <label className="block text-xs text-gray-400 mb-1">Push throttle per player (minutes)</label>
                  <input type="number" value={form.playerNotifyCooldownMinutes} onChange={(e) => setForm({ ...form, playerNotifyCooldownMinutes: e.target.value })} className="w-40 px-3 py-2 bg-gray-700 border border-gray-600 rounded text-white text-sm" />
                </div>
              </div>
            )}
          </div>

          <div className="flex gap-3">
            <button onClick={handleSubmit} className="px-4 py-2 bg-amber-500 text-black rounded hover:bg-amber-400 font-medium">{editingId ? 'Update' : 'Create'} Location</button>
            <button onClick={() => { resetForm(); setShowForm(false); }} className="px-4 py-2 bg-gray-600 text-white rounded hover:bg-gray-500">Cancel</button>
          </div>
        </div>
      )}

      {/* Locations List */}
      <div className="space-y-3">
        {locations.length > 0 ? (
          locations.map((loc) => (
            <div key={loc.id} className={`p-4 rounded-lg border ${loc.isActive ? 'bg-gray-700/50 border-gray-600' : 'bg-gray-700/20 border-gray-700'}`}>
              <div className="flex items-start justify-between">
                <div>
                  <div className="flex items-center gap-2 mb-1">
                    <MapPin size={16} className="text-green-400" />
                    <span className="text-white font-medium">{loc.name}</span>
                    <span className={`text-xs px-2 py-0.5 rounded ${loc.isActive ? 'bg-green-500/20 text-green-400' : 'bg-gray-600 text-gray-400'}`}>{loc.isActive ? 'Active' : 'Inactive'}</span>
                  </div>
                  <p className="text-sm text-gray-400">{loc.description}</p>
                  <p className="text-xs text-gray-500 mt-1">{loc.latitude}, {loc.longitude} • {loc.radius}m radius</p>
                  <div className="flex flex-wrap gap-1.5 mt-2">
                    {loc.staffNotifyEnabled && <span className="text-[11px] px-1.5 py-0.5 rounded bg-amber-500/20 text-amber-400">Staff alerts</span>}
                    {loc.playerNotifyEnabled && <span className="text-[11px] px-1.5 py-0.5 rounded bg-blue-500/20 text-blue-400">Player push</span>}
                    {loc.rewardId && <span className="text-[11px] px-1.5 py-0.5 rounded bg-green-500/20 text-green-400">Reward offer</span>}
                  </div>
                </div>
                <div className="flex gap-2">
                  <button onClick={() => handleEdit(loc)} className="p-2 rounded bg-gray-600 text-gray-300 hover:bg-gray-500"><Edit size={16} /></button>
                  <button onClick={() => handleToggle(loc.id)} className={`p-2 rounded ${loc.isActive ? 'bg-amber-500/20 text-amber-400' : 'bg-green-500/20 text-green-400'}`}>{loc.isActive ? <Pause size={16} /> : <Play size={16} />}</button>
                  <button onClick={() => handleDelete(loc.id)} className="p-2 rounded bg-red-500/20 text-red-400 hover:bg-red-500/30"><Trash2 size={16} /></button>
                </div>
              </div>
            </div>
          ))
        ) : (
          <div className="text-center py-12">
            <MapPin size={48} className="mx-auto text-gray-600 mb-4" />
            <p className="text-gray-400">No geofence locations defined</p>
            <p className="text-gray-500 text-sm mt-1">Add a location, then use the Compose tab to create notifications</p>
          </div>
        )}
      </div>
    </div>
  );
}

// Group Manager Component
function GroupManager({ groups, onRefresh }) {
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState({ name: '', description: '', color: '#D4AF37' });
  const [showMembers, setShowMembers] = useState(null);
  const [members, setMembers] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);

  const resetForm = () => {
    setForm({ name: '', description: '', color: '#D4AF37' });
    setEditingId(null);
  };

  const handleSubmit = async () => {
    if (!form.name) {
      toast.error('Name is required');
      return;
    }
    try {
      if (editingId) {
        await api.put(`/admin/user-groups/${editingId}`, form);
        toast.success('Group updated');
      } else {
        await api.post('/admin/user-groups', form);
        toast.success('Group created');
      }
      resetForm();
      setShowForm(false);
      onRefresh();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to save group');
    }
  };

  const handleEdit = (group) => {
    setForm({ name: group.name, description: group.description || '', color: group.color || '#D4AF37' });
    setEditingId(group.id);
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (!confirm('Delete this group?')) return;
    try {
      await api.delete(`/admin/user-groups/${id}`);
      toast.success('Group deleted');
      if (showMembers === id) setShowMembers(null);
      onRefresh();
    } catch (err) {
      toast.error('Failed to delete');
    }
  };

  const loadMembers = async (groupId) => {
    try {
      const response = await api.get(`/admin/user-groups/${groupId}`);
      setMembers(response.data.members || []);
      setShowMembers(groupId);
    } catch (err) {
      toast.error('Failed to load members');
    }
  };

  const searchUsers = async (query) => {
    if (!query || query.length < 2) {
      setSearchResults([]);
      return;
    }
    try {
      const response = await api.get(`/admin/users?search=${query}&limit=10`);
      setSearchResults(response.data.users || []);
    } catch (err) {
      console.error('Search failed');
    }
  };

  const addMember = async (userId) => {
    if (!showMembers) return;
    try {
      await api.post(`/admin/user-groups/${showMembers}/members`, { userIds: [userId] });
      toast.success('Member added');
      loadMembers(showMembers);
      onRefresh();
      setSearchQuery('');
      setSearchResults([]);
    } catch (err) {
      toast.error('Failed to add member');
    }
  };

  const removeMember = async (userId) => {
    if (!showMembers) return;
    try {
      await api.delete(`/admin/user-groups/${showMembers}/members`, { data: { userIds: [userId] } });
      toast.success('Member removed');
      loadMembers(showMembers);
      onRefresh();
    } catch (err) {
      toast.error('Failed to remove member');
    }
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      {/* Groups List */}
      <div className="bg-gray-800 rounded-xl p-6">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-lg font-semibold text-white">User Groups</h2>
          <button onClick={() => { resetForm(); setShowForm(!showForm); }} className="flex items-center gap-2 px-4 py-2 bg-amber-500 text-black rounded-lg hover:bg-amber-400">
            <Plus size={18} />{showForm ? 'Cancel' : 'Add Group'}
          </button>
        </div>

        {showForm && (
          <div className="mb-6 p-4 bg-gray-700/50 rounded-lg space-y-4">
            <div>
              <label className="block text-sm text-gray-400 mb-1">Name *</label>
              <input type="text" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded text-white" placeholder="Group name" />
            </div>
            <div>
              <label className="block text-sm text-gray-400 mb-1">Description</label>
              <input type="text" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded text-white" />
            </div>
            <div>
              <label className="block text-sm text-gray-400 mb-1">Color</label>
              <div className="flex gap-2">
                <input type="color" value={form.color} onChange={(e) => setForm({ ...form, color: e.target.value })} className="w-12 h-10 rounded cursor-pointer" />
                <input type="text" value={form.color} onChange={(e) => setForm({ ...form, color: e.target.value })} className="flex-1 px-3 py-2 bg-gray-700 border border-gray-600 rounded text-white" />
              </div>
            </div>
            <div className="flex gap-3">
              <button onClick={handleSubmit} className="px-4 py-2 bg-amber-500 text-black rounded hover:bg-amber-400 font-medium">{editingId ? 'Update' : 'Create'} Group</button>
              <button onClick={() => { resetForm(); setShowForm(false); }} className="px-4 py-2 bg-gray-600 text-white rounded hover:bg-gray-500">Cancel</button>
            </div>
          </div>
        )}

        <div className="space-y-3">
          {groups.length > 0 ? (
            groups.map((group) => (
              <div
                key={group.id}
                className={`p-4 rounded-lg border cursor-pointer transition ${showMembers === group.id ? 'bg-gray-700 border-amber-500' : 'bg-gray-700/50 border-gray-600 hover:border-gray-500'}`}
                onClick={() => loadMembers(group.id)}
              >
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-4 h-4 rounded-full" style={{ backgroundColor: group.color }} />
                    <div>
                      <p className="text-white font-medium">{group.name}</p>
                      <p className="text-sm text-gray-400">{group.description}</p>
                      <p className="text-xs text-gray-500 mt-1">{group.memberCount} members</p>
                    </div>
                  </div>
                  <div className="flex gap-2" onClick={(e) => e.stopPropagation()}>
                    <button onClick={() => handleEdit(group)} className="p-2 rounded bg-gray-600 text-gray-300 hover:bg-gray-500"><Edit size={16} /></button>
                    <button onClick={() => handleDelete(group.id)} className="p-2 rounded bg-red-500/20 text-red-400 hover:bg-red-500/30"><Trash2 size={16} /></button>
                  </div>
                </div>
              </div>
            ))
          ) : (
            <div className="text-center py-12">
              <UsersRound size={48} className="mx-auto text-gray-600 mb-4" />
              <p className="text-gray-400">No user groups</p>
              <p className="text-gray-500 text-sm">Click "Add Group" to create one</p>
            </div>
          )}
        </div>
      </div>

      {/* Members Panel */}
      <div className="bg-gray-800 rounded-xl p-6">
        <h2 className="text-lg font-semibold text-white mb-4">{showMembers ? 'Group Members' : 'Select a Group'}</h2>
        
        {showMembers ? (
          <div className="space-y-4">
            <div>
              <label className="block text-sm text-gray-400 mb-1">Add Member</label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" size={18} />
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => { setSearchQuery(e.target.value); searchUsers(e.target.value); }}
                  placeholder="Search users..."
                  className="w-full pl-10 pr-4 py-2 bg-gray-700 border border-gray-600 rounded text-white"
                />
              </div>
              {searchResults.length > 0 && (
                <div className="mt-2 bg-gray-700 rounded divide-y divide-gray-600 max-h-40 overflow-y-auto">
                  {searchResults.map((user) => (
                    <button key={user.id} onClick={() => addMember(user.id)} className="w-full flex items-center gap-2 p-2 hover:bg-gray-600 text-left">
                      <Plus size={14} className="text-green-400" />
                      <span className="text-white text-sm">{user.email}</span>
                      {user.firstName && <span className="text-gray-500 text-sm">({user.firstName})</span>}
                    </button>
                  ))}
                </div>
              )}
            </div>

            <div className="space-y-2 max-h-96 overflow-y-auto">
              {members.length > 0 ? (
                members.map((member) => (
                  <div key={member.id} className="flex items-center justify-between p-2 bg-gray-700/50 rounded">
                    <div>
                      <p className="text-white text-sm">{member.email}</p>
                      {member.firstName && <p className="text-xs text-gray-500">{member.firstName} {member.lastName}</p>}
                    </div>
                    <button onClick={() => removeMember(member.id)} className="p-1 text-red-400 hover:text-red-300"><XCircle size={18} /></button>
                  </div>
                ))
              ) : (
                <p className="text-center text-gray-500 py-4">No members in this group</p>
              )}
            </div>
          </div>
        ) : (
          <div className="text-center py-12">
            <Users size={48} className="mx-auto text-gray-600 mb-4" />
            <p className="text-gray-400">Click a group to manage members</p>
          </div>
        )}
      </div>
    </div>
  );
}
